import { TriangleAlert } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { DialogTitleHeader } from '@/components/molecules/dialog-title-header';
import {
  getPlatformValidationRulesForChange,
  validateSettingChanges,
} from '@/helpers/settings-validation';
import { focusDialogStart } from '@/utils/focus-dialog-start';
import { keepEscapeInDialog } from '@/utils/keep-escape-in-dialog';
import { Alert, AlertBody, AlertDescription, AlertIcon } from '@/ux-common/components/alert/alert';
import { Dialog, DialogBody, DialogContent } from '@/ux-common/components/dialog/dialog';
import { Stack } from '@/ux-common/components/stack/stack';
import { Typography } from '@/ux-common/components/typography/typography';

import { PreviewItem } from './preview-item';
import { PreviewSection } from './preview-section';
import type { SaveProgressStats } from './save-progress.utils';
import type { PreviewSaveStatus } from './setting-preview.types';
import type { SettingChangeGroup } from './settings-save.types';
import { SettingsSaveFooter } from './settings-save-footer';
import { SettingsSaveStatusMessage } from './settings-save-status-message';

export interface SettingsSaveDialogProps {
  groups: SettingChangeGroup<unknown>[];
  hasPendingSave: boolean;
  headerContent?: React.ReactNode;
  onCancelPending: () => void;
  onClose: () => void;
  onConfirm: () => void;
  onDiscard: (changeId: string) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  permissionTooltipText?: string;
  progressStats: SaveProgressStats;
  saveComplete: boolean;
  saveDisabledByPermissions?: boolean;
  saveProgressByChangeId: Record<string, PreviewSaveStatus>;
}

export const SettingsSaveDialog = ({
  groups,
  hasPendingSave,
  headerContent,
  onCancelPending,
  onClose,
  onConfirm,
  onDiscard,
  onOpenChange,
  open,
  permissionTooltipText,
  progressStats,
  saveComplete,
  saveDisabledByPermissions = false,
  saveProgressByChangeId,
}: SettingsSaveDialogProps): React.ReactNode => {
  const [isCancelled, setIsCancelled] = useState(false);

  const allChanges = useMemo(() => groups.flatMap((g) => g.changes), [groups]);

  // Run validation internally
  // TODO: Make validation pluggable via prop to support non-settings save flows.
  // Currently hardcodes getPlatformValidationRulesForChange — callers saving
  // non-settings data would need to inject their own rule resolver.
  const { issuesByChangeId, hasBlockingIssues } = useMemo(() => {
    const validationInputs = allChanges.map((change) => ({
      changeId: change.changeId,
      currentValue: change.currentValue,
      platformValidations: change.platformValidations,
    }));
    return validateSettingChanges(validationInputs, {
      getRulesForChange: getPlatformValidationRulesForChange,
    });
  }, [allChanges]);

  const changeCount = allChanges.length;
  const hasSaveProgress = Object.keys(saveProgressByChangeId).length > 0;

  useEffect(() => {
    if (!hasSaveProgress) setIsCancelled(false);
  }, [hasSaveProgress]);

  useEffect(() => {
    if (open && changeCount === 0 && !hasSaveProgress) {
      onClose();
    }
  }, [open, changeCount, onClose, hasSaveProgress]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-component="SettingsSaveDialog"
        className="dialog-lg min-h-[30vh]"
        onEscapeKeyDown={keepEscapeInDialog}
        onOpenAutoFocus={focusDialogStart}
      >
        <DialogTitleHeader title="Review & Save Changes" />
        <div className="shrink-0 space-y-3">
          <SettingsSaveStatusMessage
            hasPendingSave={hasPendingSave}
            allSucceeded={progressStats.allSucceeded}
            saveComplete={saveComplete}
            failedCount={progressStats.failed}
            cancelledCount={progressStats.cancelled}
          />
          {headerContent}
        </div>

        <DialogBody>
          {changeCount === 0 ? (
            <div className="flex items-center justify-center h-full">
              <Typography as="p" className="text-body-base text-ckds-text-fg-tier2">
                No unsaved changes
              </Typography>
            </div>
          ) : (
            <Stack className="stack-thin">
              {groups.map((group) => (
                <PreviewSection key={group.id} title={group.label}>
                  <Stack className="stack-thin">
                    {group.changes.map((change) => {
                      const rowSaveStatus = saveProgressByChangeId[change.changeId] ?? 'idle';
                      const rowIssues = issuesByChangeId[change.changeId] ?? [];
                      const warningIssue = rowIssues.find((issue) => issue.blocksSave);
                      const itemSaveStatus: PreviewSaveStatus =
                        warningIssue && rowSaveStatus === 'idle'
                          ? 'validation-warning'
                          : rowSaveStatus;
                      return (
                        <PreviewItem
                          key={change.changeId}
                          item={{
                            id: change.changeId,
                            label: change.label,
                            displayCurrent: change.displayCurrent,
                            displayOriginal: change.displayOriginal,
                            displayCurrentValue: change.displayCurrentValue,
                            displayCurrentUnits: change.displayCurrentUnits,
                          }}
                          message={change.message}
                          saveStatus={itemSaveStatus}
                          validationMessage={warningIssue?.message}
                          disableDiscard={hasPendingSave}
                          onDiscard={() => onDiscard(change.changeId)}
                        />
                      );
                    })}
                  </Stack>
                </PreviewSection>
              ))}
            </Stack>
          )}
        </DialogBody>
        {hasBlockingIssues && !hasPendingSave && (
          <div className="shrink-0">
            <Alert className="alert-caution alert-wrap">
              <AlertIcon>
                <TriangleAlert />
              </AlertIcon>
              <AlertBody>
                <AlertDescription>
                  One or more changes have validation errors. Please check the flagged values above.
                </AlertDescription>
              </AlertBody>
            </Alert>
          </div>
        )}
        <SettingsSaveFooter
          hasPendingSave={hasPendingSave}
          isCancelled={isCancelled}
          isSubmitting={hasPendingSave}
          saveComplete={saveComplete}
          allSucceeded={progressStats.allSucceeded}
          changeCount={changeCount}
          saveProgressByChangeId={saveProgressByChangeId}
          progressPercent={progressStats.progressPercent}
          disableSave={hasBlockingIssues}
          permissionTooltipText={permissionTooltipText}
          saveDisabledByPermissions={saveDisabledByPermissions}
          onCancelPending={() => {
            setIsCancelled(true);
            onCancelPending();
          }}
          onClose={onClose}
          onConfirm={() => {
            onConfirm();
          }}
        />
      </DialogContent>
    </Dialog>
  );
};
